import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createHsaReceipt,
  updateHsaReceipt,
} from "../../lib/api-modules/hsa-receipts";
import type {
  HsaReceipt,
  HsaReceiptStatus,
} from "../../lib/api-modules/types";
import { ApiError } from "../../lib/api";
import { dollarsToCents, centsToDollars } from "../../lib/util/money";
import { ErrorMessage } from "../auth/error-message";

/**
 * HSA receipt form — Phase 5 Session 5. Inline create + edit.
 */
export function HsaReceiptForm({
  accountId,
  receipt,
  onClose,
}: {
  readonly accountId: string;
  /** When provided, the form edits this receipt; otherwise it creates a new one. */
  readonly receipt?: HsaReceipt;
  readonly onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = receipt !== undefined;
  const today = new Date().toISOString().slice(0, 10);

  const [serviceDate, setServiceDate] = useState<string>(
    receipt?.service_date ?? today,
  );
  const [amount, setAmount] = useState<string>(
    receipt ? centsToDollars(receipt.amount_cents) : "",
  );
  const [provider, setProvider] = useState<string>(receipt?.provider ?? "");
  const [description, setDescription] = useState<string>(
    receipt?.description ?? "",
  );
  const [isQualified, setIsQualified] = useState<boolean>(
    receipt?.is_qualified_medical ?? true,
  );
  const [status, setStatus] = useState<HsaReceiptStatus>(
    receipt?.status ?? "unreimbursed",
  );
  const [reimbursedDate, setReimbursedDate] = useState<string>(
    receipt?.reimbursed_date ?? "",
  );
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const cents = dollarsToCents(amount);
      if (cents <= 0) throw new Error("Amount must be positive");
      if (provider.trim().length === 0) throw new Error("Provider required");
      if (status === "reimbursed" && !reimbursedDate) {
        throw new Error("Reimbursed date required when status = reimbursed");
      }
      const trimmedDescription = description.trim();
      const body = {
        service_date: serviceDate,
        amount_cents: cents,
        provider: provider.trim(),
        is_qualified_medical: isQualified,
        status,
        ...(trimmedDescription.length > 0
          ? { description: trimmedDescription }
          : {}),
        ...(status === "reimbursed"
          ? { reimbursed_date: reimbursedDate }
          : {}),
      };
      if (isEdit && receipt) {
        return updateHsaReceipt(receipt.service_date, receipt.receipt_id, body);
      }
      return createHsaReceipt({ ...body, account_id: accountId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["hsa-receipts", accountId],
      });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't save receipt.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit HSA receipt" : "Add HSA receipt"}
      className="grid grid-cols-1 gap-3 rounded-md border border-zinc-200 p-4 dark:border-zinc-800 sm:grid-cols-2"
    >
      <header className="sm:col-span-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {isEdit ? "Edit receipt" : "Add receipt"}
        </h4>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="text-xs text-zinc-500 hover:underline"
        >
          Cancel
        </button>
      </header>
      <label className="flex flex-col gap-1 text-sm">
        Service date
        <input
          type="date"
          value={serviceDate}
          onChange={(e) => setServiceDate(e.target.value)}
          required
          disabled={mutation.isPending || isEdit}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
        {isEdit && (
          <span className="text-xs text-zinc-500">
            Service date is part of the row key — re-create the receipt to
            change it.
          </span>
        )}
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Amount ($)
        <input
          type="number"
          step="0.01"
          min="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        Provider
        <input
          type="text"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          required
          disabled={mutation.isPending}
          placeholder="Mount Sinai Pediatrics"
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        Description (optional)
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={mutation.isPending}
          placeholder="Annual physical / well-child visit"
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isQualified}
          onChange={(e) => setIsQualified(e.target.checked)}
          disabled={mutation.isPending}
        />
        Qualified medical (IRS Pub 502)
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Status
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as HsaReceiptStatus)}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        >
          <option value="unreimbursed">Unreimbursed (banked)</option>
          <option value="reimbursed">Reimbursed</option>
        </select>
      </label>
      {status === "reimbursed" && (
        <label className="flex flex-col gap-1 text-sm">
          Reimbursed date
          <input
            type="date"
            value={reimbursedDate}
            onChange={(e) => setReimbursedDate(e.target.value)}
            required
            disabled={mutation.isPending}
            className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
          />
        </label>
      )}
      <div className="sm:col-span-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending
            ? "Saving…"
            : isEdit
              ? "Save changes"
              : "Add receipt"}
        </button>
      </div>
      {inlineError && (
        <div className="sm:col-span-2">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
    </form>
  );
}
